import { Button, Checkbox, Dropdown, Field, Input, makeStyles, MessageBar, MessageBarActions, MessageBarBody, Option, Text, tokens } from '@fluentui/react-components';
import { AddRegular } from "@fluentui/react-icons";
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { AppDetailsHeader } from '@tdp/common/components/AppDetailsHeader';
import { makeDocsLink } from '@tdp/common/constants';
import { useSelector } from '@tdp/core';

import { AppFeaturesActivityDataGrid } from './AppFeaturesActivityDataGrid';
import { AppFeaturesActivityDialog } from './AppFeaturesActivityDialog';
import { IAppFeaturesActivityDataGrid } from './IAppFeaturesActivityDataGrid';
import { createAppZoomHook } from '../../../hooks';
import { appDetailsSlice } from '../../../slice';
import { appFeaturesCommonStyles } from '../AppFeaturesCommon/appFeaturesCommon.styles';
import { AppFeaturesEmpty } from '../AppFeaturesCommon/AppFeaturesEmpty';
import { createAppFeaturesFooter } from '../AppFeaturesCommon/AppFeaturesFooter';




const useStyles = makeStyles({
  section: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingHorizontalM,
  },
  button: {
    marginTop: tokens.spacingVerticalXL,
    maxWidth: '300px',
  }
});

const useActivities = createAppZoomHook(app => app.activities);
const Footer = createAppFeaturesFooter(app => [app.activities]);

export const AppFeaturesActivity: React.FC = () => {
  const navigate = useNavigate();
  const baseStyles = appFeaturesCommonStyles();
  const classes = useStyles();
  const intl = useIntl();
  const appId = useParams<{ appId: string }>().appId ?? '';
  const activities = useActivities(appId);
  const isEmpty = !activities.current?.activityTypes;

  const app = useSelector((s) => appDetailsSlice.selectors.getAppById(s, appId || ""));

  const [isNewTabDialogOpen, setIsNewTabDialogOpen] = React.useState(false);
  const [activeItem, setActiveItem] = React.useState<IAppFeaturesActivityDataGrid | null>(null);


  const Empty = () => <AppFeaturesEmpty
    onClick={() => { setIsNewTabDialogOpen(true); }}
    buttonText={intl.formatMessage({
      defaultMessage: 'Create your first activity feed notification',
      description: 'Button text to add a activity feed notification'
    })}
  />

  const Warning = () => (
    <MessageBar intent="warning">
      <MessageBarBody style={{ color: tokens.colorNeutralForeground1 }}>
        <FormattedMessage
          description="warning message"
          defaultMessage="Your app needs an application (client) ID. " />
      </MessageBarBody>
      <MessageBarActions>
        <Button appearance="primary" onClick={() => {
          navigate(`/apps/${appId}/details`)
        }}>
          <FormattedMessage
            description="update button"
            defaultMessage="Update" />
        </Button>
      </MessageBarActions>
    </MessageBar>
  )

  const onDelete = (index: number) => {
    const newActivities = activities.current?.activityTypes?.filter((_, i) => i !== index);
    if (newActivities) {
      activities.update(t => {
        return {
          activityTypes: newActivities,
        }
      })
    }
  }

  const onUpdate = (item: IAppFeaturesActivityDataGrid) => {
    const newActivities = activities.current?.activityTypes?.filter((_, i) => i !== item.index);
    if (newActivities) {
      activities.update(t => {
        return {
          activityTypes: [...newActivities, item],
        }
      })
    }
    else {
      activities.update(t => {
        return {
          activityTypes: [item],
        }
      })
    }
    setActiveItem(null)
  }

  const onEdit = (index: number) => {
    const activeItem = activities.current?.activityTypes?.[index]
    setActiveItem({
      type: activeItem?.type ?? '',
      description: activeItem?.description ?? '',
      templateText: activeItem?.templateText ?? '',
      index: index,
    })
    setIsNewTabDialogOpen(true)
  }


  return (
    <div className={baseStyles.wrapper}>
      {isNewTabDialogOpen &&
        <AppFeaturesActivityDialog
          open={isNewTabDialogOpen}
          item={activeItem ?? { type: '', description: '', templateText: '', index: -1 }}
          setOpen={setIsNewTabDialogOpen}
          onUpdate={onUpdate}
        />
      }
      <div className={baseStyles.container}>
        <AppDetailsHeader
          onBackNavigation={() => { navigate(-1); }}
          showBackNavigation
          title={<FormattedMessage
            defaultMessage="Activity feed notification"
            description={'Title for Activity feed notification Section'}
          />}
          description={<FormattedMessage
            defaultMessage="Keep users informed and engaged with app notifications in the activity feed."
            description={'Description for Activity feed notification Section'}
          />}
          link={<FormattedMessage
            defaultMessage="Learn more about Activity feed notification"
            description={'Link to learn more about Activity feed notification Section'}
          />}
          href={makeDocsLink("graph/teams-send-activityfeednotifications")}
        />
        <br />

        {!app.webApplicationInfoId &&
          <section className={classes.section}>
            <Warning />
          </section>
        }

        {isEmpty ? <Empty /> :
          <section className={classes.section}>
            <AppFeaturesActivityDataGrid items={
              activities.current?.activityTypes?.map((item, index) => ({
                type: item.type ?? '',
                description: item.description ?? '',
                templateText: item.templateText ?? '',
                index: index,
              })) ?? []}
              onEdit={onEdit}
              onDelete={onDelete}
            />
            <Button
              className={classes.button}
              appearance='subtle'
              icon={<AddRegular />}
              onClick={() => { setIsNewTabDialogOpen(true); }}
            >
              <FormattedMessage
                defaultMessage="Add an activity feed notification"
                description={'Button text to add a activity feed notification'}
              />
            </Button>
          </section>
        }

      </div>
      <Footer />
    </div>
  )
}

